import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../services/api';

interface Designer {
  _id: string;
  name: string;
  email: string;
  role: string;
}

interface MediaItem {
  contentType: string;
}

interface Comment {
  _id: string;
  text: string;
  author: Designer;
  projectUpdate: string;
  createdAt: string;
  updatedAt: string;
}

interface RecentComment {
  author: Designer;
  text: string;
  createdAt: string;
}

interface ProjectUpdate {
  _id: string;
  project: string;
  designer: Designer;
  notes: string;
  media: MediaItem[];
  mediaCount: number;
  mediaUrls: string[];
  createdAt: string;
  commentsCount: number;
  recentComment?: RecentComment | null;
}

interface ProjectUpdatesState {
  updates: (ProjectUpdate & { comments?: Comment[]; commentsLoading?: boolean })[];
  loading: boolean;
  error: string | null;
}

const initialState: ProjectUpdatesState = {
  updates: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchProjectUpdates = createAsyncThunk(
  'projectUpdates/fetch',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/project-updates/${projectId}`);
      return response.data as ProjectUpdate[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch updates');
    }
  }
);

export const createProjectUpdate = createAsyncThunk(
  'projectUpdates/create',
  async (
    { projectId, notes, files, notifyClients }: { projectId: string; notes: string; files: File[]; notifyClients?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('projectId', projectId);
      formData.append('notes', notes);
      if (notifyClients !== undefined) {
        formData.append('notifyClients', notifyClients.toString());
      }

      files.forEach((file) => {
        formData.append('media', file);
      });

      const response = await api.post('/project-updates', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data as ProjectUpdate;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create update');
    }
  }
);

export const editProjectUpdate = createAsyncThunk(
  'projectUpdates/edit',
  async (
    { updateId, notes, notifyClients }: { updateId: string; notes: string; notifyClients?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/project-updates/${updateId}`, { notes, notifyClients });
      return response.data as ProjectUpdate;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to edit update');
    }
  }
);

export const deleteProjectUpdate = createAsyncThunk(
  'projectUpdates/delete',
  async (updateId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/project-updates/${updateId}`);
      return updateId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete update');
    }
  }
);

// Comment thunks
export const fetchComments = createAsyncThunk(
  'projectUpdates/fetchComments',
  async (projectUpdateId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/comments/${projectUpdateId}`);
      return { projectUpdateId, comments: response.data as Comment[] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch comments');
    }
  }
);

export const createComment = createAsyncThunk(
  'projectUpdates/createComment',
  async ({ projectUpdateId, text }: { projectUpdateId: string; text: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/comments', { projectUpdateId, text });
      return response.data as Comment;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create comment');
    }
  }
);

export const editComment = createAsyncThunk(
  'projectUpdates/editComment',
  async ({ commentId, text }: { commentId: string; text: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/comments/${commentId}`, { text });
      return response.data as Comment;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to edit comment');
    }
  }
);

export const deleteComment = createAsyncThunk(
  'projectUpdates/deleteComment',
  async (commentId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/comments/${commentId}`);
      return commentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete comment');
    }
  }
);

const projectUpdatesSlice = createSlice({
  name: 'projectUpdates',
  initialState,
  reducers: {
    clearUpdates: (state) => {
      state.updates = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch updates
      .addCase(fetchProjectUpdates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectUpdates.fulfilled, (state, action: PayloadAction<ProjectUpdate[]>) => {
        state.loading = false;
        state.updates = action.payload;
      })
      .addCase(fetchProjectUpdates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create update
      .addCase(createProjectUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProjectUpdate.fulfilled, (state, action: PayloadAction<ProjectUpdate>) => {
        state.loading = false;
        state.updates.push(action.payload);
      })
      .addCase(createProjectUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Edit update
      .addCase(editProjectUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editProjectUpdate.fulfilled, (state, action: PayloadAction<ProjectUpdate>) => {
        state.loading = false;
        const index = state.updates.findIndex((u) => u._id === action.payload._id);
        if (index !== -1) {
          state.updates[index] = { ...state.updates[index], ...action.payload };
        }
      })
      .addCase(editProjectUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete update
      .addCase(deleteProjectUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectUpdate.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.updates = state.updates.filter((u) => u._id !== action.payload);
      })
      .addCase(deleteProjectUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch comments
      .addCase(fetchComments.pending, (state, action: PayloadAction<any, string, { arg: string }>) => {
        const update = state.updates.find(u => u._id === action.meta.arg);
        if (update) {
          update.commentsLoading = true;
        }
      })
      .addCase(fetchComments.fulfilled, (state, action: PayloadAction<{ projectUpdateId: string; comments: Comment[] }>) => {
        const update = state.updates.find(u => u._id === action.payload.projectUpdateId);
        if (update) {
          update.comments = action.payload.comments;
          update.commentsLoading = false;
        }
      })
      .addCase(fetchComments.rejected, (state, action) => {
        const projectUpdateId = action.meta.arg;
        const update = state.updates.find(u => u._id === projectUpdateId);
        if (update) {
          update.commentsLoading = false;
        }
        state.error = action.payload as string;
      })
      // Create comment
      .addCase(createComment.fulfilled, (state, action: PayloadAction<Comment>) => {
        const update = state.updates.find(u => u._id === action.payload.projectUpdate);
        if (update) {
          if (!update.comments) update.comments = [];
          update.comments.push(action.payload);
        }
      })
      .addCase(createComment.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Edit comment
      .addCase(editComment.fulfilled, (state, action: PayloadAction<Comment>) => {
        const update = state.updates.find(u => u._id === action.payload.projectUpdate);
        if (update && update.comments) {
          const index = update.comments.findIndex(c => c._id === action.payload._id);
          if (index !== -1) {
            update.comments[index] = action.payload;
          }
        }
      })
      .addCase(editComment.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Delete comment
      .addCase(deleteComment.fulfilled, (state, action: PayloadAction<string>) => {
        state.updates.forEach(update => {
          if (update.comments) {
            update.comments = update.comments.filter(c => c._id !== action.payload);
          }
        });
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearUpdates, clearError } = projectUpdatesSlice.actions;
export default projectUpdatesSlice.reducer;
